// Shared commission actions.
//
// Both the Deal Management page and the Contracts page have a "Commit
// Commission" button. Historically each screen had its own copy of the
// create-a-plan logic, which drifted: one only created a plan (and mislabeled it
// "committed") while never locking it or generating payouts. This module is the
// single source of truth so the two screens can't diverge again.
//
// The real flow behind "Commit Commission":
//   1. create the plan (optionally sourcing revenue from actual delivered work)
//   2. commit it — which locks the figures and generates the per-user payout
//      records finance pays against.

import { api } from './api';
import API_URL from './apiConfig';

const API = API_URL;

// Normalize FastAPI / Pydantic error bodies to a single string.
export function extractDetail(err) {
  if (!err || err.detail == null) return '';
  const d = err.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((e) => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  return JSON.stringify(d);
}

// Fetch a deal's actual delivered revenue from the contract ledger.
// Returns the rollup object, or null when unavailable (e.g. permission/tenant),
// so callers can fall back to manual entry without hard-failing.
export async function fetchAccruedRevenue(dealId) {
  try {
    const res = await api.get(
      `${API}/api/commission-management/deals/${dealId}/accrued-revenue`,
    );
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// Build the create-plan payload from a commissionForm. When the form's
// revenue_source is "contract_actuals" the backend pulls revenue from the ledger
// and ignores the numeric revenue here.
export function buildPlanPayload(commissionForm) {
  const useActuals = commissionForm.revenue_source === 'contract_actuals';
  return {
    deal_id: commissionForm.deal_id,
    template_id: commissionForm.template_id || null,
    revenue: parseFloat(commissionForm.revenue) || 0,
    revenue_source: useActuals ? 'contract_actuals' : 'manual',
    cost_type: commissionForm.cost_type,
    cost_value: parseFloat(commissionForm.cost_value) || 0,
    tiers: (commissionForm.tiers || []).map((tier) => ({
      tier_number: tier.tier_number,
      tier_name: tier.tier_name,
      percentage: tier.percentage,
      users: (tier.users || [])
        .filter((u) => u.user_id)
        .map((u) => ({
          user_id: u.user_id,
          user_name: u.user_name,
          split_percentage: parseFloat(u.split_percentage) || 0,
          commission_amount: 0, // computed by the backend
        })),
    })),
  };
}

// Create a commission plan and immediately commit it (lock + generate payouts).
// Returns { commission_id, payouts_generated }. Throws Error with a clear
// message; if the plan is created but the commit fails, the message says so.
export async function createAndCommitPlan(payload) {
  const createRes = await api.post(`${API}/api/commission-management/plans`, payload);
  if (!createRes.ok) {
    const err = await createRes.json().catch(() => ({}));
    throw new Error(extractDetail(err) || 'Failed to create commission plan');
  }
  const created = await createRes.json();
  const commissionId = created.commission_id;

  const commitRes = await api.post(
    `${API}/api/commission-management/plans/${commissionId}/commit`,
  );
  if (!commitRes.ok) {
    const err = await commitRes.json().catch(() => ({}));
    throw new Error(`Plan created but not committed: ${extractDetail(err) || 'commit failed'}`);
  }
  const committed = await commitRes.json();
  return {
    commission_id: commissionId,
    payouts_generated: committed.payouts_generated ?? 0,
  };
}

// Convenience: the success toast text both screens should show.
export function committedToast(payoutsGenerated) {
  const n = payoutsGenerated ?? 0;
  return `Commission committed — ${n} payout${n === 1 ? '' : 's'} created`;
}

// ---- Post-commit lifecycle: view payouts, settle, sync, undo --------------

// List the per-user payout records generated for a committed plan.
export async function listPayouts(commissionId) {
  const res = await api.get(
    `${API}/api/commission-management/plans/${commissionId}/payouts`,
  );
  if (!res.ok) {
    throw new Error(extractDetail(await res.json().catch(() => ({}))) || 'Failed to load payouts');
  }
  return res.json();
}

// Record a payment against a payout. Omit `amount` to pay the full remaining
// balance. Returns the updated payout summary.
export async function markPayoutPaid(payoutId, amount) {
  const body = amount == null ? {} : { amount };
  const res = await api.post(
    `${API}/api/commission-management/payouts/${payoutId}/mark-paid`,
    body,
  );
  if (!res.ok) {
    throw new Error(extractDetail(await res.json().catch(() => ({}))) || 'Payment failed');
  }
  return res.json();
}

// Refresh a plan's revenue from actual delivered work (only valid before/after
// a lock — a committed plan returns 409 with a clear message).
export async function syncPlanRevenue(commissionId) {
  const res = await api.post(
    `${API}/api/commission-management/plans/${commissionId}/sync-revenue`,
  );
  if (!res.ok) {
    throw new Error(extractDetail(await res.json().catch(() => ({}))) || 'Sync failed');
  }
  return res.json();
}

// Undo a commit: reopens the plan, removes its pending payouts, and detaches it
// from the deal. Blocked (409) by the backend if any payout has been paid.
export async function uncommitPlan(commissionId) {
  const res = await api.post(
    `${API}/api/commission-management/plans/${commissionId}/uncommit`,
  );
  if (!res.ok) {
    throw new Error(extractDetail(await res.json().catch(() => ({}))) || 'Uncommit failed');
  }
  return res.json();
}
